import { initializeApp, getApps } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';
import { api } from './api';

/**
 * Web push (desktop browsers and Android Chrome) through the site's Firebase project.
 * The server (App\Ops\OpsNotifier) sends to every token registered for the account.
 * push.native.ts is the phone-app version.
 */
export type PushState = 'unsupported' | 'default' | 'granted' | 'denied';

const SAVED = 'ops_push_token';

const config = {
  apiKey: process.env.EXPO_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.EXPO_PUBLIC_FIREBASE_APP_ID,
};

let listening = false;

async function messaging() {
  if (!config.apiKey || !(await isSupported().catch(() => false))) return null;
  const app = getApps()[0] ?? initializeApp(config);
  return getMessaging(app);
}

export async function pushState(): Promise<PushState> {
  if (typeof Notification === 'undefined' || !('serviceWorker' in navigator) || !(await messaging())) return 'unsupported';
  return Notification.permission as PushState;
}

// Never leave a button spinning: the browser's prompt or Firebase can hang.
const within = <T,>(p: Promise<T>, ms: number, fallback: T) =>
  Promise.race([p, new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms))]);

/** Asks for permission (needs a tap) and registers this browser for the account. */
export async function enablePush(): Promise<PushState> {
  const state = await pushState();
  if (state === 'unsupported') return state;
  const permission = state === 'default' ? await within(Notification.requestPermission(), 60000, 'default') : state;
  if (permission !== 'granted') return permission as PushState;
  await within(register().catch(() => {}), 20000, undefined);
  return 'granted';
}

/**
 * After sign-in: registers right away when allowed before; otherwise asks once, on the first
 * tap anywhere (browsers only show the permission prompt after a tap), with no card or message.
 */
export async function refreshPush(): Promise<void> {
  const state = await pushState();
  if (state === 'granted') {
    await register().catch(() => {});
  } else if (state === 'default' && typeof document !== 'undefined') {
    const ask = () => {
      document.removeEventListener('pointerdown', ask, true);
      enablePush().catch(() => {});
    };
    document.addEventListener('pointerdown', ask, true);
  }
}

export async function disablePush(): Promise<void> {
  const token = localStorage.getItem(SAVED);
  if (token) {
    await api('/ops/push-token', { method: 'DELETE', body: { token } }).catch(() => {});
    localStorage.removeItem(SAVED);
  }
  const m = await messaging();
  if (m) await deleteToken(m).catch(() => {});
}

async function register() {
  const m = await messaging();
  if (!m) return;
  const sw = await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' });
  await navigator.serviceWorker.ready;
  const token = await getToken(m, { vapidKey: process.env.EXPO_PUBLIC_FIREBASE_VAPID_KEY, serviceWorkerRegistration: sw });
  if (!token) return;
  await api('/ops/push-token', { body: { token, platform: 'web-app' } });
  localStorage.setItem(SAVED, token);

  // While the app is open the browser doesn't show pushes by itself: show them the same way.
  if (!listening) {
    listening = true;
    onMessage(m, (payload) => {
      const n = payload.notification ?? {};
      sw.showNotification(n.title ?? 'متابعة الطلبات', {
        body: n.body ?? '',
        icon: '/favicon.ico',
        data: payload.data ?? {},
        tag: payload.data?.order_id ? `order-${payload.data.order_id}` : undefined,
      });
    });
  }
}
